import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { api } from '../../api/client';
import './Account.css';

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const TIER_ICONS = { Wanderer:'�', Explorer:'🧭', 'Forest Keeper':'🌲', 'Mountain Guardian':'⛰️', 'Legend of the Peaks':'👑' };
const TIER_COLORS = { Wanderer:'#7a9478', Explorer:'#0C6B37', 'Forest Keeper':'#094A25', 'Mountain Guardian':'#c9853a', 'Legend of the Peaks':'#F8B324' };

function fmtDate(d) { const x = new Date(d); return MONTHS[x.getMonth()] + ' ' + x.getDate() + ', ' + x.getFullYear(); }
function nights(a, b) { return Math.round((new Date(b) - new Date(a)) / 864e5); }
function bkStatus(b) {
  const t = new Date(); t.setHours(0,0,0,0);
  if (b.status === 'cancelled') return 'cancelled';
  if (t < new Date(b.check_in)) return 'upcoming';
  if (t < new Date(b.check_out)) return 'active';
  return 'completed';
}

export default function AccountPage() {
  const { user, signOut, updateProfile } = useAuth();
  const navigate = useNavigate();
  const [fullName, setFullName] = useState('');
  const [phone, setPhone] = useState('');
  const [saving, setSaving] = useState(false);
  const [profileMsg, setProfileMsg] = useState({ type: '', text: '' });
  const [bookings, setBookings] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('upcoming');
  const [cancellingId, setCancellingId] = useState(null);

  useEffect(() => { if (user) { setFullName(user.full_name || ''); setPhone(user.phone || ''); } }, [user]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try { const [br, sr] = await Promise.all([api.getMyBookings(), api.getMyStats()]); setBookings(br.bookings || []); setStats(sr); } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSave = async (e) => {
    e.preventDefault(); setSaving(true); setProfileMsg({ type: '', text: '' });
    const { error } = await updateProfile({ full_name: fullName, phone });
    if (error) setProfileMsg({ type: 'error', text: error.message });
    else { setProfileMsg({ type: 'success', text: 'Profile updated successfully!' }); setTimeout(() => setProfileMsg({ type: '', text: '' }), 3000); }
    setSaving(false);
  };

  const handleCancel = async (id) => {
    if (!window.confirm('Cancel this booking?')) return;
    setCancellingId(id);
    try { await api.cancelBooking(id); fetchData(); } catch (e) { alert(e.message); }
    setCancellingId(null);
  };

  const doSignOut = async () => { await signOut(); navigate('/login'); };

  const memberSince = user?.created_at ? new Date(user.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : '—';

  const filtered = bookings.filter(b => {
    const s = bkStatus(b);
    if (tab === 'upcoming') return s === 'upcoming' || s === 'active';
    if (tab === 'past') return s === 'completed';
    return s === 'cancelled';
  });

  const loyalty = stats?.loyalty;

  return (
    <div className="acc page-enter">
      {/* ── Hero Banner ── */}
      <section className="acc__hero">
        <div className="acc__hero-bg">
          <div className="acc__hero-overlay" />
          <div className="acc__hero-leaf acc__hero-leaf--1">🍃</div>
          <div className="acc__hero-leaf acc__hero-leaf--2">🌿</div>
          <div className="acc__hero-leaf acc__hero-leaf--3">🍂</div>
        </div>
        <div className="acc__hero-content" data-reveal>
          <span className="acc__hero-tag">
            <span className="acc__hero-tag-line" />
            My Account
            <span className="acc__hero-tag-line" />
          </span>
          <h1 className="acc__hero-title">
            Welcome back, <span className="acc__hero-name">{user?.full_name || 'Traveler'}</span>
          </h1>
          <p className="acc__hero-desc">Manage your mountain retreats and track your journey with us</p>
        </div>
      </section>

      {/* ── Stats Ribbon ── */}
      {stats && (
        <section className="acc__stats-section" data-reveal>
          <div className="acc__stats">
            <div className="acc__stat">
              <span className="acc__stat-icon">🏡</span>
              <span className="acc__stat-num">{stats.totalBookings}</span>
              <span className="acc__stat-label">Total Stays</span>
            </div>
            <div className="acc__stat-sep" />
            <div className="acc__stat">
              <span className="acc__stat-icon">🌙</span>
              <span className="acc__stat-num">{stats.totalNights}</span>
              <span className="acc__stat-label">Nights Spent</span>
            </div>
            <div className="acc__stat-sep" />
            <div className="acc__stat">
              <span className="acc__stat-icon">📅</span>
              <span className="acc__stat-num">{stats.upcoming}</span>
              <span className="acc__stat-label">Upcoming</span>
            </div>
            <div className="acc__stat-sep" />
            <div className="acc__stat">
              <span className="acc__stat-icon">👥</span>
              <span className="acc__stat-num">{stats.totalGuests}</span>
              <span className="acc__stat-label">Guests Hosted</span>
            </div>
            {stats.favouriteMonth && (
              <>
                <div className="acc__stat-sep" />
                <div className="acc__stat">
                  <span className="acc__stat-icon">⭐</span>
                  <span className="acc__stat-num acc__stat-num--text">{stats.favouriteMonth}</span>
                  <span className="acc__stat-label">Favourite Month</span>
                </div>
              </>
            )}
          </div>
        </section>
      )}

      {/* ── Main Grid ── */}
      <section className="acc__main">
        <div className="acc__grid">
          {/* LEFT COLUMN */}
          <div className="acc__left">
            {/* Loyalty Card */}
            {loyalty && (
              <div className="acc__card acc__loyalty" data-reveal="left">
                <div className="acc__card-head">
                  <span className="acc__card-tag">🌿 Loyalty Program</span>
                </div>
                <div className="acc__loyalty-tier">
                  <div className="acc__loyalty-sigil" style={{ borderColor: TIER_COLORS[loyalty.tier], background: TIER_COLORS[loyalty.tier] + '12' }}>
                    <span className="acc__loyalty-icon">{TIER_ICONS[loyalty.tier]}</span>
                  </div>
                  <div className="acc__loyalty-info">
                    <h3 style={{ color: TIER_COLORS[loyalty.tier] }}>{loyalty.tier}</h3>
                    {loyalty.discount > 0 && (
                      <div className="acc__loyalty-badge" style={{ borderColor: TIER_COLORS[loyalty.tier] + '44', color: TIER_COLORS[loyalty.tier], background: TIER_COLORS[loyalty.tier] + '0a' }}>
                        {loyalty.discount}% DISCOUNT
                      </div>
                    )}
                  </div>
                </div>
                {loyalty.nextTier && (
                  <div className="acc__loyalty-progress">
                    <div className="acc__loyalty-bar">
                      <div className="acc__loyalty-bar-fill" style={{ width: loyalty.progress + '%', background: 'linear-gradient(90deg, ' + TIER_COLORS[loyalty.tier] + '88, ' + TIER_COLORS[loyalty.tier] + ')' }} />
                    </div>
                    <p className="acc__loyalty-next">
                      {loyalty.bookingsToNext} more stay{loyalty.bookingsToNext !== 1 ? 's' : ''} to <strong style={{ color: TIER_COLORS[loyalty.nextTier] }}>{loyalty.nextTier}</strong>
                    </p>
                  </div>
                )}
                <div className="acc__loyalty-roadmap">
                  {Object.entries(TIER_ICONS).map(([tier, icon], idx) => {
                    const th = [0, 3, 5, 10, 20];
                    const reached = stats.totalBookings >= th[idx];
                    return (
                      <div key={tier} className={'acc__loyalty-step' + (reached ? ' acc__loyalty-step--done' : '') + (tier === loyalty.tier ? ' acc__loyalty-step--active' : '')}>
                        <span className="acc__loyalty-step-icon">{icon}</span>
                        <span className="acc__loyalty-step-name">{tier}</span>
                        <span className="acc__loyalty-step-req">{th[idx]}+ stays</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Profile Card */}
            <div className="acc__card acc__profile" data-reveal="left">
              <div className="acc__card-head">
                <span className="acc__card-tag">👤 Profile</span>
                <button className="acc__signout" onClick={doSignOut}>Sign Out</button>
              </div>
              <div className="acc__profile-info">
                <div className="acc__profile-row">
                  <span className="acc__profile-row-icon">✉️</span>
                  <div>
                    <span className="acc__profile-row-label">Email</span>
                    <span className="acc__profile-row-val">{user?.email || '—'}</span>
                  </div>
                </div>
                <div className="acc__profile-row">
                  <span className="acc__profile-row-icon">📆</span>
                  <div>
                    <span className="acc__profile-row-label">Member Since</span>
                    <span className="acc__profile-row-val">{memberSince}</span>
                  </div>
                </div>
              </div>
              <form onSubmit={handleSave} className="acc__profile-form">
                {profileMsg.text && <div className={'acc__msg acc__msg--' + profileMsg.type}>{profileMsg.text}</div>}
                <div className="acc__profile-fields">
                  <div className="acc__field">
                    <label>Full Name</label>
                    <input type="text" value={fullName} onChange={e => setFullName(e.target.value)} placeholder="Your name" />
                  </div>
                  <div className="acc__field">
                    <label>Phone</label>
                    <input type="tel" value={phone} onChange={e => setPhone(e.target.value)} placeholder="+40 712 345 678" />
                  </div>
                </div>
                <button className="acc__btn acc__btn--save" type="submit" disabled={saving}>
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </form>
            </div>
          </div>

          {/* RIGHT COLUMN */}
          <div className="acc__right" data-reveal="right">
            <div className="acc__card acc__bookings">
              <div className="acc__card-head">
                <span className="acc__card-tag">🗓️ My Journeys</span>
                <Link to="/book" className="acc__btn acc__btn--new">+ New Booking</Link>
              </div>
              <div className="acc__tabs">
                {[
                  { key: 'upcoming', label: 'Upcoming', count: bookings.filter(b => { const s = bkStatus(b); return s === 'upcoming' || s === 'active'; }).length },
                  { key: 'past', label: 'History', count: bookings.filter(b => bkStatus(b) === 'completed').length },
                  { key: 'cancelled', label: 'Cancelled', count: bookings.filter(b => bkStatus(b) === 'cancelled').length },
                ].map(t => (
                  <button key={t.key} className={'acc__tab' + (tab === t.key ? ' acc__tab--active' : '')} onClick={() => setTab(t.key)}>
                    {t.label}
                    {t.count > 0 && <span className="acc__tab-count">{t.count}</span>}
                  </button>
                ))}
              </div>
              <div className="acc__booking-list">
                {loading ? (
                  <div className="acc__empty">
                    <div className="acc__spinner" />
                    <p>Loading your journeys...</p>
                  </div>
                ) : filtered.length === 0 ? (
                  <div className="acc__empty">
                    <span className="acc__empty-icon">{tab === 'upcoming' ? '🌄' : tab === 'past' ? '📖' : '💨'}</span>
                    <p>{tab === 'upcoming' ? 'No upcoming journeys' : tab === 'past' ? 'No past journeys yet' : 'No cancelled bookings'}</p>
                    {tab === 'upcoming' && <Link to="/book" className="acc__btn acc__btn--outline">Book a Stay</Link>}
                  </div>
                ) : filtered.map(bk => {
                  const st = bkStatus(bk);
                  const n = nights(bk.check_in, bk.check_out);
                  return (
                    <div key={bk.id} className={'acc__booking acc__booking--' + st}>
                      <div className="acc__booking-badge">
                        <span className="acc__booking-badge-day">{new Date(bk.check_in).getDate()}</span>
                        <span className="acc__booking-badge-month">{MONTHS[new Date(bk.check_in).getMonth()]}</span>
                      </div>
                      <div className="acc__booking-body">
                        <div className="acc__booking-range">{fmtDate(bk.check_in)} → {fmtDate(bk.check_out)}</div>
                        <div className="acc__booking-meta">
                          <span>🌙 {n} night{n > 1 ? 's' : ''}</span>
                          <span>👥 {bk.guests}</span>
                          {bk.notes && <span title={bk.notes}>📝</span>}
                        </div>
                      </div>
                      <div className="acc__booking-end">
                        <span className={'acc__booking-status acc__booking-status--' + st}>
                          {st === 'active' ? '● Active' : st === 'upcoming' ? 'Upcoming' : st === 'completed' ? '✓ Done' : '✕ Cancelled'}
                        </span>
                        {st === 'upcoming' && (
                          <button className="acc__booking-cancel" onClick={() => handleCancel(bk.id)} disabled={cancellingId === bk.id}>
                            {cancellingId === bk.id ? '...' : 'Cancel'}
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}